import * as DialogPrimitive from "@radix-ui/react-dialog";
import { ArrowRight, Check, Loader2, X } from "lucide-react";
import * as React from "react";
import { createContext, useCallback, useContext, useState } from "react";
import { useSubscription } from "@/hooks/use-subscription";
import {
	type BillingPeriod,
	getMonthlyPrice,
	getPublicPlanTier,
	SUBSCRIPTION_DEFINITIONS,
	type SubscriptionTier,
} from "@/lib/plans";
import { startSubscriptionCheckout } from "@/lib/subscription-checkout";
import { cn } from "@/lib/utils";

type PaidSubscriptionTier = Exclude<SubscriptionTier, "free">;

// Context for programmatic dialog control
interface ProDialogOpenOptions {
	tier?: PaidSubscriptionTier;
	billingPeriod?: BillingPeriod;
}

interface ProDialogContextValue {
	openProDialog: (
		options?: ProDialogOpenOptions | React.SyntheticEvent,
	) => void;
	closeProDialog: () => void;
	isOpen: boolean;
}

const ProDialogContext = createContext<ProDialogContextValue | null>(null);
const PLAN_TIERS = [
	"pro",
	"max",
] as const satisfies readonly PaidSubscriptionTier[];
const STATIC_PLAN_HIGHLIGHTS: Record<PaidSubscriptionTier, string[]> = {
	pro: [
		"15 connected social accounts",
		"100 scheduled posts per month",
		"3 workspaces and 3 team members",
		"Draft, schedule, and publish from chat",
		"Calendar queue with per-platform captions",
		"Email support",
	],
	max: [
		"150 connected social accounts",
		"1,000 scheduled posts per month",
		"30 workspaces and 30 team members",
		"Draft, schedule, and publish from chat",
		"Priority scheduling capacity for busy calendars",
		"Team approvals",
	],
};
const PLAN_ACCENTS: Record<
	PaidSubscriptionTier,
	{ badge: string; tone: string }
> = {
	pro: {
		badge: "Steady publishing",
		tone: "Enough accounts, posts, workspaces, and seats to run a steady multi-platform content calendar.",
	},
	max: {
		badge: "More capacity",
		tone: "More accounts, scheduled posts, and shared workspaces for higher-volume accounts.",
	},
};

function yearlySavingsEUR(tier: PaidSubscriptionTier): number {
	const sub = SUBSCRIPTION_DEFINITIONS[tier];
	return Math.max(sub.priceEUR * 12 - sub.yearlyPriceEUR, 0);
}

export function useProDialog() {
	const context = useContext(ProDialogContext);
	if (!context) {
		throw new Error("useProDialog must be used within a ProDialogProvider");
	}
	return context;
}

interface ProDialogProviderProps {
	children: React.ReactNode;
}

export function ProDialogProvider({ children }: ProDialogProviderProps) {
	const [isOpen, setIsOpen] = useState(false);
	const [initialTier, setInitialTier] = useState<
		PaidSubscriptionTier | undefined
	>(undefined);
	const [initialBillingPeriod, setInitialBillingPeriod] = useState<
		BillingPeriod | undefined
	>(undefined);

	const openProDialog = useCallback(
		(options?: ProDialogOpenOptions | React.SyntheticEvent) => {
			// Accept raw events from `onClick={openProDialog}` — only tier/billingPeriod
			// on a plain options object are used.
			const opts =
				options && typeof options === "object" && !("nativeEvent" in options)
					? (options as ProDialogOpenOptions)
					: undefined;
			setInitialTier(opts?.tier);
			setInitialBillingPeriod(opts?.billingPeriod);
			setIsOpen(true);
		},
		[],
	);

	const closeProDialog = useCallback(() => {
		setIsOpen(false);
	}, []);

	return (
		<ProDialogContext.Provider
			value={{ openProDialog, closeProDialog, isOpen }}
		>
			{children}
			<ProDialogContent
				open={isOpen}
				onOpenChange={setIsOpen}
				initialTier={initialTier}
				initialBillingPeriod={initialBillingPeriod}
			/>
		</ProDialogContext.Provider>
	);
}

interface ProDialogContentProps {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	initialTier?: PaidSubscriptionTier;
	initialBillingPeriod?: BillingPeriod;
	/** When false, the dialog cannot be dismissed (no close button, no escape, no outside click) */
	closable?: boolean;
}

function ProDialogContent({
	open,
	onOpenChange,
	initialTier,
	initialBillingPeriod,
	closable = true,
}: ProDialogContentProps) {
	const [selectedTier, setSelectedTier] = useState<PaidSubscriptionTier>(
		initialTier ?? "pro",
	);
	const [billingPeriod, setBillingPeriod] = useState<BillingPeriod>(
		initialBillingPeriod ?? "monthly",
	);
	const [loadingTier, setLoadingTier] = useState<PaidSubscriptionTier | null>(
		null,
	);
	const { isPaid, tier: currentTier } = useSubscription();

	const [prevOpen, setPrevOpen] = useState(open);
	const [prevInitialTier, setPrevInitialTier] = useState(initialTier);
	const [prevInitialBillingPeriod, setPrevInitialBillingPeriod] =
		useState(initialBillingPeriod);
	if (
		open !== prevOpen ||
		initialTier !== prevInitialTier ||
		initialBillingPeriod !== prevInitialBillingPeriod
	) {
		setPrevOpen(open);
		setPrevInitialTier(initialTier);
		setPrevInitialBillingPeriod(initialBillingPeriod);
		if (open && initialTier) setSelectedTier(initialTier);
		if (open && initialBillingPeriod) setBillingPeriod(initialBillingPeriod);
		if (!open) setLoadingTier(null);
	}

	const resetCheckoutState = React.useCallback(() => {
		setLoadingTier(null);
	}, []);

	React.useEffect(() => {
		const resetIfPageIsActive = () => {
			if (document.visibilityState === "visible") resetCheckoutState();
		};

		window.addEventListener("pageshow", resetCheckoutState);
		window.addEventListener("focus", resetCheckoutState);
		document.addEventListener("visibilitychange", resetIfPageIsActive);

		return () => {
			window.removeEventListener("pageshow", resetCheckoutState);
			window.removeEventListener("focus", resetCheckoutState);
			document.removeEventListener("visibilitychange", resetIfPageIsActive);
		};
	}, [resetCheckoutState]);

	const handleUpgrade = async (tierToUpgrade: PaidSubscriptionTier) => {
		if (loadingTier) return;
		setSelectedTier(tierToUpgrade);
		setLoadingTier(tierToUpgrade);
		try {
			const publicPlan = getPublicPlanTier(tierToUpgrade);
			const checkoutUrl = await startSubscriptionCheckout({
				plan: tierToUpgrade,
				billingPeriod,
				successUrl: `${window.location.origin}/upgrade-success?plan=${publicPlan}`,
				cancelUrl: `${window.location.origin}/upgrade-error?reason=cancelled&plan=${publicPlan}&billingPeriod=${billingPeriod}`,
			});
			window.location.assign(checkoutUrl);
			window.setTimeout(resetCheckoutState, 1500);
		} catch (error) {
			console.error("[ProDialog] Upgrade exception:", error);
			window.location.href = `/upgrade-error?reason=failed&plan=${tierToUpgrade}&billingPeriod=${billingPeriod}`;
		}
	};
	return (
		<DialogPrimitive.Root
			open={open}
			onOpenChange={closable ? onOpenChange : undefined}
		>
			<DialogPrimitive.Portal>
				<DialogPrimitive.Overlay className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0" />
				<DialogPrimitive.Content
					className="fixed left-[50%] top-[50%] z-50 w-full max-w-[780px] translate-x-[-50%] translate-y-[-50%] px-4 duration-200 outline-none data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95"
					{...(!closable && {
						onEscapeKeyDown: (e: Event) => e.preventDefault(),
						onPointerDownOutside: (e: Event) => e.preventDefault(),
						onInteractOutside: (e: Event) => e.preventDefault(),
					})}
				>
					<DialogPrimitive.Title className="sr-only">
						AgentReacher pricing
					</DialogPrimitive.Title>
					<div className="relative max-h-[90vh] overflow-y-auto rounded-lg border border-border bg-card shadow-xl">
						{closable && (
							<DialogPrimitive.Close className="absolute right-3 top-3 rounded-md p-1.5 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus:outline-none focus:ring-2 focus:ring-ring">
								<X className="size-4" />
								<span className="sr-only">Close</span>
							</DialogPrimitive.Close>
						)}

						<div className="border-b border-border px-6 py-5">
							<h3 className="text-base font-semibold tracking-tight text-foreground">
								Choose your plan
							</h3>
							<p className="mt-1 text-sm text-muted-foreground">
								Compare Pro and Max before starting checkout. Full refund within
								30 days if it is not for you.
							</p>
						</div>

						<div className="flex items-center justify-center gap-2 border-b border-border bg-muted/20 px-6 py-3">
							<div className="inline-flex rounded-full border border-border bg-muted/40 p-1 text-sm">
								<button
									type="button"
									onClick={() => setBillingPeriod("monthly")}
									className={cn(
										"rounded-full px-3 py-1 font-medium transition-colors",
										billingPeriod === "monthly"
											? "bg-background text-foreground shadow-sm"
											: "text-muted-foreground hover:text-foreground",
									)}
								>
									Monthly
								</button>
								<button
									type="button"
									onClick={() => setBillingPeriod("yearly")}
									className={cn(
										"rounded-full px-3 py-1 font-medium transition-colors",
										billingPeriod === "yearly"
											? "bg-background text-foreground shadow-sm"
											: "text-muted-foreground hover:text-foreground",
									)}
								>
									Annually
									<span className="ml-1 text-xs font-medium text-blue-600 dark:text-blue-400">
										2 months free
									</span>
								</button>
							</div>
						</div>

						<div className="grid gap-3 px-4 py-4 sm:grid-cols-2 sm:px-6 sm:py-6">
							{PLAN_TIERS.map((planTier) => {
								const plan = SUBSCRIPTION_DEFINITIONS[planTier];
								const monthlyPrice = getMonthlyPrice(planTier, billingPeriod);
								const displayPrice =
									billingPeriod === "yearly"
										? plan.yearlyPriceEUR
										: monthlyPrice;
								const highlights = STATIC_PLAN_HIGHLIGHTS[planTier] ?? [];
								const yearlySavings = yearlySavingsEUR(planTier);
								const isFocused = selectedTier === planTier;
								const isHighlighted = !!plan.highlighted;
								const isCurrentPaidPlan = isPaid && currentTier === planTier;
								const isBusy = loadingTier === planTier;
								const disabled = Boolean(loadingTier) || isCurrentPaidPlan;

								return (
									<div
										key={planTier}
										className={cn(
											"flex min-h-[430px] flex-col rounded-lg border bg-background p-4 transition-colors",
											isFocused && "border-blue-500/50 ring-1 ring-blue-500/20",
											isHighlighted &&
												"bg-[radial-gradient(ellipse_110%_70%_at_50%_0%,rgba(37,99,235,0.08)_0%,transparent_68%)]",
										)}
									>
										<div className="flex items-center justify-between gap-3">
											<div>
												<h4 className="text-lg font-semibold tracking-tight text-foreground">
													{plan.name}
												</h4>
												<p className="mt-1 text-xs font-medium text-muted-foreground">
													{PLAN_ACCENTS[planTier].badge}
												</p>
											</div>
											{isHighlighted && (
												<span className="inline-flex shrink-0 items-center rounded-full bg-emerald-100 px-2 py-0.5 text-[11px] font-medium text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-400">
													Popular
												</span>
											)}
										</div>

										<p className="mt-3 min-h-12 text-sm leading-relaxed text-muted-foreground">
											{PLAN_ACCENTS[planTier].tone}
										</p>

										<div className="mt-5 flex items-baseline gap-1">
											<span className="text-lg font-medium text-foreground">
												€
											</span>
											<span className="text-4xl font-semibold tracking-normal text-foreground">
												{displayPrice}
											</span>
											<span className="text-xs font-medium text-muted-foreground">
												{billingPeriod === "yearly" ? "/yr" : "/mo"}
											</span>
										</div>
										<p className="mt-1 text-xs text-muted-foreground">
											{billingPeriod === "yearly"
												? `€${monthlyPrice}/mo equivalent. Pay for 10 months, get 2 months free and save €${yearlySavings}/yr.`
												: "Monthly billing. Cancel anytime."}
										</p>

										<ul className="mt-5 mb-8 space-y-2.5">
											{highlights.map((feature) => (
												<li
													key={feature}
													className="flex items-start gap-3 text-sm text-foreground"
												>
													<Check
														className={cn(
															"mt-0.5 size-4 shrink-0",
															isHighlighted
																? "text-blue-600 dark:text-blue-400"
																: "text-muted-foreground",
														)}
													/>
													<span>{feature}</span>
												</li>
											))}
										</ul>

										<button
											type="button"
											onMouseEnter={() => setSelectedTier(planTier)}
											onFocus={() => setSelectedTier(planTier)}
											onClick={() => {
												void handleUpgrade(planTier);
											}}
											disabled={disabled}
											className={cn(
												"mt-auto inline-flex w-full items-center justify-center gap-2 rounded-md px-4 py-2.5 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-60",
												isHighlighted
													? "bg-blue-600 text-white hover:bg-blue-700"
													: "bg-primary text-primary-foreground hover:bg-primary/90",
											)}
										>
											{isBusy ? (
												<>
													<Loader2 className="size-4 animate-spin" />
													Redirecting…
												</>
											) : isCurrentPaidPlan ? (
												"Current plan"
											) : (
												<>
													{isPaid ? "Upgrade to" : "Subscribe to"} {plan.name}
													<ArrowRight className="size-4" />
												</>
											)}
										</button>
									</div>
								);
							})}
						</div>
					</div>
				</DialogPrimitive.Content>
			</DialogPrimitive.Portal>
		</DialogPrimitive.Root>
	);
}

export { ProDialogContent };
